import type { FilePlottingContext, PlottedPagePdf } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { Media, PlotShape, PlottingContext } from "@pdfking/renderer-api"
import { CreateCompletePage, CreateContext } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"
import { KeyDrop, RecordMerge } from "@pdfking/tfunc"

import type { RequestMedia } from "#public"

import { PlotMixin as mixin } from "../mixins/PlotMixin"
import { PlotShapes } from "./PlotShapes"

/**
 * A plotted page drawn as the plot's own shapes, once, as a def every media
 * showing it `<use>`s (see MediaDefs): laid out as its raster would be — the
 * trim grown by the bleed, y down from the bleed's top-left corner — so a
 * media places it the same either way. Its shapes draw with its own colors,
 * layers and files, as the engine plots it, and its own media resolve against
 * its own `file_map`: a plotted page inside it is drawn the same way, a split
 * page by its raster.
 */
export const PlotMediaDef = CreateRenderFunction({
  computed: {
    page_context(): PlottingContext<unknown> {
      return CreateContext(
        RecordMerge(
          KeyDrop(this.context, ["color_map", "layer_map", "file_map"]),
          this.plot.context
        )
      )
    },
    // completed with its own context's defaults, as the engine plots it
    shapes(): PlotShape[] {
      return CreateCompletePage(this.plot.page, this.page_context).shapes
    },
    // y up from the trim's bottom-left, as a page's shapes are written, inside
    // the raster's extent
    transform(): string {
      const { left, top } = this.media.bleed
      return `translate(${this.to_px(left)} ${this.to_px(top)}) matrix(1 0 0 -1 0 ${this.get_h(this.media.trim)})`
    },
  },
  mixins: [mixin] as const,
  props: {
    guid: "" as StringGUID,
    media: {} as Media,
    media_for: undefined as unknown as (context: FilePlottingContext) => RequestMedia,
    plot: {} as PlottedPagePdf,
  },
  render() {
    return (
      <g id={this.guid} transform={this.transform}>
        <PlotShapes
          context={this.page_context}
          printable
          request_media={this.media_for(this.plot.context)}
          shapes={this.shapes}
        />
      </g>
    )
  },
})
